/**
 * @fileoverview PHARMAKON — Trésorerie : le store à clés du temps réel (TWP-11).
 * @module lib/tresorerie/store
 *
 * 02_ARCHITECTURE §11, ADR-11 — TA-08 (contrat d'invalidation), TA-18 (version validée avec
 * l'argent), TD-60 (sujet sur le flux unique + plancher de version), TD-61 (zustand, aucune
 * dépendance nouvelle), UX 01 §13 / 03UX P-12 (les chiffres restent lisibles pendant la relecture).
 *
 *   · UNE ENTRÉE PAR LECTURE. Une vue montée lit une ressource (`cleRequete(ressource, params)`) et
 *     déclare les clés d'invalidation dont elle dépend (`depend: ["compte:<id>", "operations"]`).
 *     Les données vivent ici, pas dans l'écran : deux vues de la même lecture partagent la même
 *     requête (dédupliquée), une vue démontée puis remontée réaffiche aussitôt la dernière lecture.
 *   · INVALIDATION CIBLÉE. Une trame du serveur (`{ sujet: "treso", keys, v }`, publiée APRÈS le
 *     commit) ou la réponse d'une écriture de cet onglet (`appliquerEcriture({ touched, … })`)
 *     invalide les entrées dont une dépendance correspond ; seules les entrées MONTÉES se relisent
 *     (chacune son petit point d'accès : une tuile, une page de 50 lignes, un tiroir), les autres
 *     sont marquées périmées et relues à leur prochain montage. Aucun jeu de données n'est sondé.
 *   · LE PLANCHER (`GET /version`, ETag faible, 304 sans corps). Ce qu'une trame perdue aurait dû
 *     dire est rattrapé à la vérification suivante : 60 s onglet visible, 5 min caché, au retour
 *     d'onglet et à chaque `resync` du flux. Une version qui a bougé SANS qu'une trame l'ait
 *     annoncée invalide toutes les entrées (on ne sait pas lesquelles) — c'est rare et borné.
 *   · LES CHIFFRES RESTENT LISIBLES. Une relecture garde la donnée affichée (`rafraichit: true`) ;
 *     une erreur de relecture garde la dernière donnée et pose `erreur` ; une réponse plus ancienne
 *     qu'une relecture lancée après elle est ignorée (numéro de séquence).
 *
 * Le module expose une FABRIQUE (`creerStoreTresorerie`, dépendances injectées — testable sans
 * réseau ni DOM) et le store unique de l'application (`storeTresorerie`).
 */
import { createStore } from "zustand/vanilla";
import {
  CLE_TRESORERIE_JOKER,
  CLE_TRESORERIE_SEPARATEUR,
  TEMPS_REEL_TRESORERIE_CLIENT as CFG,
} from "../../config/tresorerie.config.js";

// ─── Clés ────────────────────────────────────────────────────────────────────────────────────────

/** JSON stable (clés d'objet triées) — deux `params` égaux donnent la même entrée. */
function stable(valeur) {
  if (Array.isArray(valeur)) return `[${valeur.map(stable).join(",")}]`;
  if (valeur && typeof valeur === "object") {
    return `{${Object.keys(valeur)
      .filter((k) => valeur[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stable(valeur[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(valeur ?? null);
}

/**
 * La clé de cache d'une lecture : `['treso', ressource, params]` sérialisé de façon stable.
 * @param {string} ressource ex. « compte », « operations », « kpi »
 * @param {object} [params]
 */
export function cleRequete(ressource, params = {}) {
  return stable(["treso", String(ressource), params ?? {}]);
}

function decouper(cle) {
  const i = cle.indexOf(CLE_TRESORERIE_SEPARATEUR);
  return i === -1 ? [cle, null] : [cle.slice(0, i), cle.slice(i + 1)];
}

/**
 * Une clé publiée touche-t-elle cette dépendance ? Égalité exacte ; ou même famille quand l'un des
 * deux côtés est le joker de famille (`compte:*` — une vue de tous les comptes, ou une trame repliée
 * par le serveur au-delà de `max_cles`).
 */
export function cleCorrespond(dependance, publiee) {
  if (dependance === publiee) return true;
  const [fd, id] = decouper(dependance);
  const [fp, ip] = decouper(publiee);
  if (fd !== fp || id === null || ip === null) return false;
  return id === CLE_TRESORERIE_JOKER || ip === CLE_TRESORERIE_JOKER;
}

/** Une des dépendances est-elle touchée par une des clés publiées ? */
export function dependancesTouchees(depend = [], publiees = []) {
  return depend.some((d) => publiees.some((p) => cleCorrespond(d, p)));
}

function enBigInt(v) {
  if (v == null || v === "") return null;
  try {
    return BigInt(String(v));
  } catch {
    return null;
  }
}

const ENTREE_VIDE = Object.freeze({
  data: undefined,
  erreur: null,
  statut: "vide", // vide → chargement → pret | erreur
  rafraichit: false,
  perime: false,
  lu_at: null,
  abonnes: 0,
});

// ─── La fabrique ─────────────────────────────────────────────────────────────────────────────────

/**
 * @param {object} deps
 * @param {(p: { etag: string|null }) => Promise<object>} deps.lireVersion `tresoreriePharmacieApi.version`
 * @param {(prefixe: string, rappel: Function) => () => void} deps.ecouter `ecouterSujet` du flux unique
 * @param {{ setTimeout: Function, clearTimeout: Function, now: () => number }} [deps.horloge]
 * @param {Document|null} [deps.document] pour la visibilité de l'onglet (null hors navigateur)
 */
export function creerStoreTresorerie({
  lireVersion,
  ecouter,
  horloge = { setTimeout: (...a) => setTimeout(...a), clearTimeout: (t) => clearTimeout(t), now: () => Date.now() },
  document: doc = typeof document === "undefined" ? null : document,
} = {}) {
  const store = createStore(() => ({
    /** cle → entrée (voir ENTREE_VIDE) — l'état que les écrans lisent. */
    entrees: {},
    /** La version de trésorerie lue au plancher (chaîne), l'instant serveur de la lecture. */
    version: null,
    verifie_at: null,
  }));

  // Hors état réactif : ce qui ne s'affiche pas.
  const chargeurs = new Map(); // cle → { charger(signal?), depend: string[] }
  const enCours = new Map(); // cle → { seq, relire: boolean }
  const oublis = new Map(); // cle → minuterie d'oubli
  let sequence = 0;
  let etag = null;
  let versionTrames = null; // bigint — la plus haute version annoncée par une trame reçue
  let plancher = { visible: CFG.plancher_visible_ms, cache: CFG.plancher_cache_ms };
  const ecrituresLocales = []; // [{ v: bigint, cles: Set<string> }]
  let montees = 0;
  let desabonner = null;
  let minuterie = null;
  let verification = null;

  const lireEntree = (cle) => store.getState().entrees[cle];
  function majEntree(cle, patch) {
    store.setState((s) => {
      const avant = s.entrees[cle] ?? ENTREE_VIDE;
      return { entrees: { ...s.entrees, [cle]: { ...avant, ...patch } } };
    });
  }

  // ── Lectures ──────────────────────────────────────────────────────────────────────────────────

  /** (Re)lit une entrée — dédupliquée ; une invalidation pendant le vol relance une lecture après. */
  async function charger(cle) {
    const c = chargeurs.get(cle);
    if (!c) return;
    const vol = enCours.get(cle);
    if (vol) {
      vol.relire = true;
      return;
    }
    const seq = ++sequence;
    const etat = { seq, relire: false };
    enCours.set(cle, etat);
    const avant = lireEntree(cle);
    const aDesDonnees = avant && avant.data !== undefined;
    majEntree(cle, aDesDonnees ? { rafraichit: true } : { statut: "chargement" });
    try {
      const data = await c.charger();
      if (enCours.get(cle)?.seq === seq) {
        majEntree(cle, { data, erreur: null, statut: "pret", rafraichit: false, perime: false, lu_at: horloge.now() });
      }
    } catch (erreur) {
      if (enCours.get(cle)?.seq === seq) {
        // La dernière donnée reste affichée ; l'écran montre l'erreur à côté (P-12).
        majEntree(cle, { erreur, statut: aDesDonnees ? "pret" : "erreur", rafraichit: false });
      }
    } finally {
      if (enCours.get(cle)?.seq === seq) enCours.delete(cle);
    }
    if (etat.relire && (lireEntree(cle)?.abonnes ?? 0) > 0) await charger(cle);
  }

  /** Les entrées touchées : relues si montées, marquées périmées sinon. */
  function invaliderEntrees(predicat) {
    const montees_ = [];
    const perimees = [];
    for (const [cle, e] of Object.entries(store.getState().entrees)) {
      const c = chargeurs.get(cle);
      if (!predicat(cle, c)) continue;
      if (e.abonnes > 0) montees_.push(cle);
      else perimees.push(cle);
    }
    if (perimees.length > 0) {
      store.setState((s) => {
        const entrees = { ...s.entrees };
        for (const cle of perimees) entrees[cle] = { ...entrees[cle], perime: true };
        return { entrees };
      });
    }
    for (const cle of montees_) charger(cle);
    return montees_.length + perimees.length;
  }

  /** Invalide les entrées dont une dépendance correspond à l'une des clés publiées. */
  function invalider(cles) {
    if (!Array.isArray(cles) || cles.length === 0) return 0;
    return invaliderEntrees((_cle, c) => dependancesTouchees(c?.depend ?? [], cles));
  }

  function invaliderTout() {
    return invaliderEntrees(() => true);
  }

  // ── Écritures de cet onglet, trames du serveur ────────────────────────────────────────────────

  /**
   * L'écran qui écrit applique SA réponse tout de suite (`touched`, `version_tresorerie` de la
   * réponse) ; la trame correspondante, quand elle arrive, ne relit pas une seconde fois.
   */
  function appliquerEcriture({ touched = [], version_tresorerie = null } = {}) {
    const v = enBigInt(version_tresorerie);
    if (v != null) {
      ecrituresLocales.push({ v, cles: new Set(touched) });
      while (ecrituresLocales.length > CFG.ecritures_locales_max) ecrituresLocales.shift();
    }
    return invalider(touched);
  }

  /** Une trame `{ sujet: "treso", keys, v }` du flux de l'officine. */
  function recevoirTrame(data) {
    const cles = Array.isArray(data?.keys) ? data.keys.filter((k) => typeof k === "string" && k) : [];
    const v = enBigInt(data?.v);
    if (v != null && (versionTrames == null || v > versionTrames)) versionTrames = v;
    // Déjà appliquée par la réponse de l'écriture de cet onglet (même version, mêmes clés) ?
    if (v != null) {
      const locale = ecrituresLocales.find((e) => e.v === v);
      if (locale && cles.every((k) => locale.cles.has(k))) return 0;
    }
    return invalider(cles);
  }

  // ── Le plancher de version ────────────────────────────────────────────────────────────────────

  /**
   * `GET /version` conditionnel. Une version différente de la dernière lue, et que les trames
   * reçues n'ont pas annoncée, invalide tout ce qui est en mémoire.
   */
  function verifierVersion() {
    if (verification) return verification;
    verification = (async () => {
      try {
        const r = await lireVersion({ etag });
        if (!r || r.inchange) {
          store.setState({ verifie_at: new Date(horloge.now()).toISOString() });
          return { change: false };
        }
        const avant = store.getState().version;
        const v = enBigInt(r.version_tresorerie);
        if (r.plancher_ms?.visible > 0 && r.plancher_ms?.cache > 0) plancher = { ...r.plancher_ms };
        etag = r.etag ?? null;
        store.setState({ version: r.version_tresorerie ?? null, verifie_at: r.verifie_at ?? null });
        const precedente = enBigInt(avant);
        // Première lecture : aucune référence — les trames du flux, ouvert avant les lectures,
        // couvrent ce qui s'est écrit depuis.
        if (precedente == null || v == null || v === precedente) return { change: false };
        const annoncee = v > precedente && versionTrames != null && v <= versionTrames;
        if (!annoncee) invaliderTout();
        return { change: !annoncee };
      } catch {
        // Le plancher réessaiera à la prochaine échéance ; les données affichées restent.
        return { change: false, erreur: true };
      } finally {
        verification = null;
      }
    })();
    return verification;
  }

  /** `resync` du flux (tampon dépassé, reconnexion sans reprise) : tout relire, puis le plancher. */
  function resync() {
    invaliderTout();
    return verifierVersion();
  }

  const visible = () => !doc || doc.visibilityState !== "hidden";

  function planifier() {
    if (minuterie) horloge.clearTimeout(minuterie);
    minuterie = horloge.setTimeout(async () => {
      minuterie = null;
      await verifierVersion();
      if (montees > 0) planifier();
    }, visible() ? plancher.visible : plancher.cache);
  }

  function surVisibilite() {
    if (visible()) verifierVersion();
    planifier();
  }

  function demarrer() {
    desabonner = ecouter(CFG.sujet, (evt) => {
      if (evt?.resync) return resync();
      // `ecouterSujet` filtre par préfixe : seul le sujet exact de la trésorerie compte.
      return evt?.sujet === CFG.sujet ? recevoirTrame(evt.data) : 0;
    });
    doc?.addEventListener?.("visibilitychange", surVisibilite);
    verifierVersion();
    planifier();
  }

  function arreter() {
    desabonner?.();
    desabonner = null;
    doc?.removeEventListener?.("visibilitychange", surVisibilite);
    if (minuterie) horloge.clearTimeout(minuterie);
    minuterie = null;
  }

  // ── Montage des vues ──────────────────────────────────────────────────────────────────────────

  /**
   * Une vue monte une lecture. Rend la fonction de démontage.
   * @param {string} cle `cleRequete(…)`
   * @param {{ charger: () => Promise<any>, depend?: string[] }} lecture
   */
  function monter(cle, { charger: fn, depend = [] }) {
    chargeurs.set(cle, { charger: fn, depend: [...depend] });
    const t = oublis.get(cle);
    if (t) {
      horloge.clearTimeout(t);
      oublis.delete(cle);
    }
    const avant = lireEntree(cle);
    majEntree(cle, { abonnes: (avant?.abonnes ?? 0) + 1 });
    montees += 1;
    if (montees === 1) demarrer();
    if (!avant || avant.data === undefined || avant.perime || avant.statut === "erreur") charger(cle);

    let monte = true;
    return function demonter() {
      if (!monte) return;
      monte = false;
      const e = lireEntree(cle);
      const restants = Math.max(0, (e?.abonnes ?? 1) - 1);
      majEntree(cle, { abonnes: restants });
      montees = Math.max(0, montees - 1);
      if (montees === 0) arreter();
      if (restants === 0) {
        oublis.set(
          cle,
          horloge.setTimeout(() => {
            oublis.delete(cle);
            if ((lireEntree(cle)?.abonnes ?? 0) > 0) return;
            chargeurs.delete(cle);
            enCours.delete(cle);
            store.setState((s) => {
              const { [cle]: _oubliee, ...reste } = s.entrees;
              return { entrees: reste };
            });
          }, CFG.conservation_ms),
        );
      }
    };
  }

  /** Une vue déjà montée change de chargeur (nouvelle fermeture) sans relire. */
  function mettreAJourLecture(cle, { charger: fn, depend }) {
    const c = chargeurs.get(cle);
    if (!c) return;
    chargeurs.set(cle, { charger: fn ?? c.charger, depend: depend ? [...depend] : c.depend });
  }

  return {
    store,
    monter,
    mettreAJourLecture,
    charger,
    invalider,
    invaliderTout,
    appliquerEcriture,
    recevoirTrame,
    verifierVersion,
    resync,
    /** Tests et déconnexion : tout oublier. */
    reinitialiser() {
      arreter();
      for (const t of oublis.values()) horloge.clearTimeout(t);
      oublis.clear();
      chargeurs.clear();
      enCours.clear();
      ecrituresLocales.length = 0;
      montees = 0;
      etag = null;
      versionTrames = null;
      plancher = { visible: CFG.plancher_visible_ms, cache: CFG.plancher_cache_ms };
      store.setState({ entrees: {}, version: null, verifie_at: null });
    },
  };
}
